import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom, take } from 'rxjs';
import { TournamentsResponse } from '../../../all-tournaments/models/api/responses/tournaments.response';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import {
    GenresResponse, PlatformsResponse,
} from '../../models/api/responses/games.response';
import { Game, GameFormat } from '../../models/games.interface';
import { GamesService } from '../../services/games.service';
import { GamesActions } from '../actions/actions-type.action';
import { RegionsListResponse } from 'src/app/all-tournaments/models/api/responses/regions.response';
import { Store } from '@ngrx/store';
import { GamesSelectors } from '../../store/selectors/selectors-type.selector';
import { TOURNAMENTV2 } from '../../../shared/constants';

@Injectable()
export class GamesEffect {
    private actions$ = inject(Actions);
    private gamesService = inject(GamesService);
    private store = inject(Store);

    checkInGameAccountId = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.checkUniqueAccountInGameId),
            switchMap(({ payload }) =>
                this.gamesService
                    .checkInGameAccountId(payload.gameId, payload.inGameId)
                    .pipe(
                        map(() =>
                            GamesActions.checkUniqueAccountInGameIdSuccess({
                                payload: true,
                            })
                        ),
                        catchError(() =>
                            of(
                                GamesActions.checkUniqueAccountInGameIdFailure({
                                    payload: false,
                                })
                            )
                        )
                    )
            )
        )
    );

    fetchGames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchGames),
            exhaustMap(() =>
                this.gamesService.getGamesList().pipe(
                    map((response: any) =>
                        GamesActions.fetchGamesSuccess({
                            payload: response.items,
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            GamesActions.fetchGamesFailure({
                                payload: response,
                            })
                        )
                    )
                )
            )
        )
    );

    fetchGenres$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchGenres),
            exhaustMap(() =>
                this.gamesService.fetchGenres().pipe(
                    map((response: GenresResponse) =>
                        GamesActions.fetchGenresSuccess({
                            payload: response,
                        })
                    ),
                    catchError(() => of(GamesActions.fetchGenresFailure()))
                )
            )
        )
    );
    fetchPlatforms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchPlatforms),
            exhaustMap(() =>
                this.gamesService.fetchPlatforms().pipe(
                    map((response: PlatformsResponse) =>
                        GamesActions.fetchPlatformsSuccess({
                            payload: response,
                        })
                    ),
                    catchError(() => of(GamesActions.fetchPlatformsFailure()))
                )
            )
        )
    );


    fetchFormats$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchFormats),
            switchMap(({ payload }) =>
                this.gamesService.fetchFormats(payload.gameId).pipe(
                    map((formats: GameFormat[]) =>
                        GamesActions.fetchFormatsSuccess({
                            payload: {
                                gameId: payload.gameId,
                                formats: formats,
                            }
                        })
                    ),
                    catchError(() => of(GamesActions.fetchFormatsFailure()))
                )
            )
        )
    );

    getGameById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.getGameById),
            switchMap(({ payload }) =>
                this.gamesService.getGameById(payload.id).pipe(
                    map((response: Game) =>
                        GamesActions.getGameByIdSuccess({
                            payload: {
                                data: response,
                                openCrateTournamentModal: Boolean(
                                    payload.isV2TournamentCreate || payload.openCrateTournamentModal
                                ),
                                isV2TournamentCreate: Boolean(
                                    payload.isV2TournamentCreate
                                )
                            },
                        })
                    ),
                    catchError(() => of(GamesActions.getGameByIdFailure()))
                )
            )
        )
    );

    getGameByIdV1$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.getGameByIdV1),
            switchMap(({ payload }) =>
                this.gamesService.getGameByIdV1(payload.id).pipe(
                    map((response: Game) =>
                        GamesActions.getGameByIdV1Success({
                            payload: {
                                data: response,
                                openCrateTournamentModal: Boolean(payload.openCrateTournamentModal),
                            },
                        })
                    ),
                    catchError(() => of(GamesActions.getGameByIdV1Failure()))
                )
            )
        )
    );

    fetchAllTournamentsByGame$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchTournamentsByGame),
            switchMap(({  payload: {
                    patchExistingTournaments,
                    type,
                    gameId,
                    page,
                    filters = {},
                } }) =>
                this.store.select(GamesSelectors.selectGameById(gameId)).pipe(
                    take(1),
                    switchMap((game) => {
                        const useV2Base = (game as any)?.gameBucketType === TOURNAMENTV2;
                        return this.gamesService
                            .getTournamentsByGame(page, gameId, filters, useV2Base)
                            .pipe(
                                map((response: TournamentsResponse) =>
                                    GamesActions.fetchTournamentsByGameSuccess({
                                        payload: {
                                            response,
                                            type,
                                            patchExistingTournaments:
                                                patchExistingTournaments ?? false,
                                        },
                                    })
                                ),
                                catchError((response: BadResponseWithError) =>
                                    of(
                                        GamesActions.fetchTournamentsByGameFailure({
                                            payload: response,
                                        })
                                    )
                                )
                            );
                    })
                )
            )
        )
    );

    fetchRegions$ = createEffect(() =>
            this.actions$.pipe(
                ofType(GamesActions.fetchRegions),
                switchMap(() =>
                    this.gamesService.getRegions().pipe(
                        map((response: RegionsListResponse) =>
                            GamesActions.fetchRegionsSuccess({
                                payload: response,
                            })
                        )
                    )
                )
            )
        );
    fetchRegionsV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchRegionsV2),
            switchMap(() =>
                this.gamesService.getRegionsV2().pipe(
                    map((response: RegionsListResponse) =>
                        GamesActions.fetchRegionsV2Success({
                            payload: response,
                        })
                    )
                )
            )
        )
    );

    fetchAllTournamentsByGameV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchTournamentsByGameV2),
            switchMap(({  payload: {
                patchExistingTournaments,
                type,
                gameId,
                page,
                filters = {},
            } }) =>
                this.gamesService
                    .getTournamentsByGame(page, gameId, filters, true)
                    .pipe(
                        map((response: TournamentsResponse) =>
                            GamesActions.fetchTournamentsByGameV2Success({
                                payload: {
                                    response,
                                    type,
                                    patchExistingTournaments:
                                        patchExistingTournaments ?? false,
                                },
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                GamesActions.fetchTournamentsByGameV2Failure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );
}
